/* eslint-env browser */
// Push notifications › New notification (wave 3, W2_Portal_Settings look):
// one card with the message (title, text), who gets it (user type, device
// type, also by SMS), and when (now, or a date and time in Addis Ababa on a
// 12-hour clock, once or on repeat). One label above each field, errors
// under the field, one button.
//
// Sends, exactly as before:
// - Now (critical edits on): sendNotification({ ...message, sendSMS,
//   createdBy, createdByName }, { requestId, force }) with ONE request id per
//   submission (reused for "Send anyway"), so a repeated request can never
//   create a second campaign; the Sent-tab row editNotifications({ ...message,
//   sendSMS }, 'Add') is written only once the server accepted it (not for a
//   duplicate or an already-queued send). An identical message sent recently
//   asks before sending again; the result names what was queued, then opens
//   Notifications › Delivery.
// - Later (critical edits on): scheduleNotification({ ...message, sendSMS,
//   scheduledAt (epoch ms), status: 'scheduled', createdAt: now, recurring? })
//   or, when editing, editScheduledNotification({ ...same, id }).
// New: a question that names the audience before a send now or a schedule
// (it reaches everyone in that group). The date and time are entered in
// Addis Ababa time (the old field used the computer's clock and zone).
// Only staff who may send notifications reach this form, as before.
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Navigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { api } from 'common';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import MenuItem from '@mui/material/MenuItem';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import ScheduleRoundedIcon from '@mui/icons-material/ScheduleRounded';
import RepeatRoundedIcon from '@mui/icons-material/RepeatRounded';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import { tokens as c } from '../theme';
import { showToast, confirmDialog, InlineMessage } from '../components/feedback';
import { formatAddisDateTime } from '../common/exportUtils';
import { BackButton, SectionCard, FieldLabel, FieldError, FilterChip, AddisDateTimeField, InfoRow, calmCaps, formatNumber } from '../components/modern';
import { canManagePushNotifications } from '../common/notificationSecurity';

const DAY_LABELS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
const QUICK_HOURS = [1, 3, 6, 12, 24];
const MINUTE = 60000;

const AddNotifications = () => {
  const { t, i18n } = useTranslation();
  const settings = useSelector((state) => state.settingsdata.settings);
  const auth = useSelector((state) => state.auth);
  const canManage = canManagePushNotifications(auth.profile);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { sendNotification, editNotifications, scheduleNotification } = api;
  const { state } = useLocation();
  const [loding, setLoding] = useState(false);
  const [sendSMS, setSendSMS] = useState(false);
  const [sendMode, setSendMode] = useState('now');
  const [scheduledAt, setScheduledAt] = useState(null); // epoch ms (Addis Ababa wall time entered)
  const [recurrenceFreq, setRecurrenceFreq] = useState('none');
  const [recurrenceDays, setRecurrenceDays] = useState([]);
  // One id per submission attempt, reused if the same attempt is retried or
  // confirmed past the duplicate warning. The server derives the campaign id
  // from it, so a replayed request cannot create a second campaign — the old
  // form awaited a fan-out that could take minutes, so a browser timeout plus
  // a second click sent the whole audience twice.
  const [requestId, setRequestId] = useState(null);
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState(null);
  const isEditing = state?.editData ? true : false;
  const [data, setData] = useState({
    createdAt: new Date().getTime(),
    body: '',
    devicetype: '',
    title: '',
    usertype: '',
  });

  useEffect(() => {
    if (state?.editData) {
      const ed = state.editData;
      setData({
        createdAt: ed.createdAt || new Date().getTime(),
        body: ed.body || '',
        devicetype: ed.devicetype || '',
        title: ed.title || '',
        usertype: ed.usertype || '',
      });
      setSendSMS(ed.sendSMS || false);
      setSendMode('schedule');
      if (ed.scheduledAt) {
        setScheduledAt(Math.floor(Number(ed.scheduledAt) / MINUTE) * MINUTE);
      }
      if (ed.recurring) {
        setRecurrenceFreq(ed.recurring.frequency || 'none');
        setRecurrenceDays(ed.recurring.days || []);
      }
    }
  }, [state]);

  const L = (key, dflt, opts) => t(key, { defaultValue: dflt, ...(opts || {}) });
  const required = L('form_required', 'This field is required');

  const setField = (name, value) => {
    setData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: null }));
    if (formError) setFormError(null);
  };

  const handleQuickDelay = (hours) => {
    setScheduledAt(Math.floor((Date.now() + hours * 60 * 60 * 1000) / MINUTE) * MINUTE);
    setSendMode('schedule');
    setErrors((prev) => ({ ...prev, scheduledAt: null }));
  };

  const newRequestId = () =>
    typeof window !== 'undefined' && window.crypto && typeof window.crypto.randomUUID === 'function'
      ? window.crypto.randomUUID()
      : 'rid-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);

  // Who gets it, in words: "Drivers on Android", "All customers".
  const deviceWord = data.devicetype === 'ANDROID' ? t('android') : data.devicetype === 'IOS' ? t('ios') : null;
  const groupWord = data.usertype === 'driver' ? t('drivers') : data.usertype === 'customer' ? t('riders') : '';
  const audience = deviceWord
    ? L('notification_audience_device', '{{group}} on {{device}}', { group: groupWord, device: deviceWord })
    : L('notification_audience_all', 'All {{group}}', { group: String(groupWord).toLowerCase() });

  const preview = (
    <Box component="dl" sx={{ m: 0 }}>
      <InfoRow label={t('title')}>{data.title}</InfoRow>
      <InfoRow label={t('body')}>
        <Box component="span" sx={{ whiteSpace: 'pre-wrap' }}>
          {data.body}
        </Box>
      </InfoRow>
      <InfoRow label={t('sms', { defaultValue: 'SMS' })}>{sendSMS ? t('yes') : t('no')}</InfoRow>
    </Box>
  );

  // Fire the send and interpret the verdict. Resolves to the server's JSON.
  // `force` is only ever true when the operator has read the duplicate warning
  // and chosen to send anyway.
  const performSend = async (rid, force) => {
    const payload = {
      ...data,
      sendSMS,
      createdBy: auth.profile?.uid || null,
      createdByName: [auth.profile?.firstName, auth.profile?.lastName].filter(Boolean).join(' ') || null,
    };
    const result = await dispatch(sendNotification(payload, { requestId: rid, force: force === true }));
    if (result && result.duplicate === true) {
      return { duplicate: { requestId: rid, minutesAgo: result.minutesAgo, message: result.message }, result };
    }
    if (result && result.success === false) {
      setFormError(result.message || t('notification_send_failed', { defaultValue: 'Could not queue this notification.' }));
      return { result };
    }
    // Only record the Sent-tab row once the server has actually accepted the
    // campaign — a duplicate-rejected submit must not leave a "sent" row behind.
    if (!result || result.deduped !== true) {
      dispatch(editNotifications({ ...data, sendSMS }, 'Add'));
    }
    return { sent: result || {} };
  };

  // Queued, not delivered — and it says so, because delivery now takes as
  // long as it takes and is watchable instead of guessed at.
  const showResult = async (sendResult) => {
    const dup = sendResult.audience && (sendResult.audience.duplicateDevices || sendResult.audience.duplicatePhones);
    const ok = await confirmDialog({
      title: sendResult.deduped ? t('already_queued_title', { defaultValue: 'Already queued' }) : t('queued_title', { defaultValue: 'Queued for delivery' }),
      body: (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, color: c.INK_2 }}>
          {sendResult.deduped || sendResult.message ? (
            <Typography variant="body1">
              {sendResult.deduped ? t('already_queued_body', { defaultValue: 'This notification was already queued — nothing was sent twice.' }) : sendResult.message}
            </Typography>
          ) : null}
          {dup ? (
            <Typography variant="body2">
              {t('duplicates_collapsed', { defaultValue: 'Collapsed to one message each:' })} {formatNumber(sendResult.audience.duplicateDevices || 0)}{' '}
              {t('shared_devices', { defaultValue: 'shared device(s)' })}, {formatNumber(sendResult.audience.duplicatePhones || 0)}{' '}
              {t('shared_numbers', { defaultValue: 'repeated number(s)' })}.
            </Typography>
          ) : null}
          <Typography variant="body2">{t('watch_delivery_hint', { defaultValue: 'Follow live progress under Notifications → Delivery.' })}</Typography>
        </Box>
      ),
      confirmLabel: t('ok', { defaultValue: 'OK' }),
      cancelLabel: null,
    });
    if (ok) navigate('/notifications', { state: { openDelivery: true } });
    else navigate('/notifications');
  };

  const runSend = async (rid, force) => {
    setLoding(true);
    let outcome = null;
    try {
      outcome = await performSend(rid, force);
    } catch (err) {
      setFormError(err.message || t('notification_send_failed', { defaultValue: 'Could not queue this notification.' }));
    } finally {
      setLoding(false);
    }
    if (!outcome) return;
    if (outcome.duplicate) {
      // Nothing has been sent at this point. The server recognised an
      // identical title/body/audience going out recently and stopped — the
      // case a per-submission id cannot catch, because a reload or a second
      // tab produces a new one. Confirming re-sends the SAME submission id,
      // so this can still only ever create one campaign.
      const again = await confirmDialog({
        title: t('duplicate_notification_title', { defaultValue: 'Send this again?' }),
        body:
          t('duplicate_notification_body', { defaultValue: 'An identical notification went out' }) +
          (typeof outcome.duplicate.minutesAgo === 'number' ? ' ' + t('minutes_ago', { count: outcome.duplicate.minutesAgo, defaultValue: '{{count}} minute(s) ago' }) : '') +
          '. ' +
          t('duplicate_notification_hint', { defaultValue: 'Recipients would receive the same message twice. Send it anyway only if that is what you intend.' }),
        confirmLabel: t('send_anyway', { defaultValue: 'Send anyway' }),
        cancelLabel: L('confirm_cancel', 'Cancel'),
        destructive: true,
      });
      if (again) runSend(outcome.duplicate.requestId, true);
      return;
    }
    if (outcome.sent) showResult(outcome.sent);
  };

  const validate = () => {
    const next = {};
    if (!data.title) next.title = required;
    if (!data.body) next.body = required;
    if (!data.usertype) next.usertype = required;
    if (!data.devicetype) next.devicetype = required;
    if (sendMode === 'schedule') {
      if (!scheduledAt) next.scheduledAt = t('select_schedule_time');
      else if (scheduledAt <= Date.now()) next.scheduledAt = t('schedule_time_past');
      if (recurrenceFreq === 'custom' && recurrenceDays.length === 0) next.recurrenceDays = t('select_recurrence_days');
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handelSubmit = async () => {
    setFormError(null);
    if (!validate()) return;
    if (!settings.AllowCriticalEditsAdmin) {
      setFormError(t('demo_mode'));
      return;
    }
    if (sendMode === 'now') {
      const go = await confirmDialog({
        title: L('notification_confirm_send', 'Send to {{audience}} now?', { audience }),
        body: preview,
        confirmLabel: t('send_now'),
        cancelLabel: L('confirm_cancel', 'Cancel'),
      });
      if (!go) return;
      // Awaited, unlike before: the operator needs the verdict (queued, already
      // queued, or an identical message sent recently) BEFORE we navigate away.
      const rid = requestId || newRequestId();
      setRequestId(rid);
      runSend(rid, false);
      return;
    }

    const go = await confirmDialog({
      title: L('notification_confirm_schedule', 'Send to {{audience}} on {{when}}?', { audience, when: formatAddisDateTime(scheduledAt) }),
      body: preview,
      confirmLabel: isEditing ? t('update_schedule') : t('schedule_notification'),
      cancelLabel: L('confirm_cancel', 'Cancel'),
    });
    if (!go) return;
    const recurring = recurrenceFreq !== 'none' ? { frequency: recurrenceFreq, days: recurrenceFreq === 'custom' ? recurrenceDays : [] } : null;
    const payload = {
      ...data,
      sendSMS,
      scheduledAt: scheduledAt,
      status: 'scheduled',
      createdAt: new Date().getTime(),
      ...(recurring ? { recurring } : {}),
    };
    if (isEditing && state.editData.id) {
      payload.id = state.editData.id;
      dispatch(api.editScheduledNotification(payload));
    } else {
      dispatch(scheduleNotification(payload));
    }
    showToast.success(isEditing ? L('notification_schedule_updated', 'Schedule updated') : L('notification_scheduled', 'Notification scheduled'));
    navigate('/notifications');
  };

  // Route-level permits are role-based only, so re-check the capability here:
  // a call-centre supervisor without the flag may reach the URL but not the form.
  if (!canManage) {
    return <Navigate to="/notifications" replace />;
  }

  const field = { minWidth: 0 };
  const describedBy = (name) => (errors[name] ? `notify-${name}-error` : undefined);
  const quickSelected = (h) => !!scheduledAt && Math.abs(scheduledAt - (Date.now() + h * 3600000)) < MINUTE;
  const submitLabel = sendMode === 'now' ? t('send_now') : isEditing ? t('update_schedule') : t('schedule_notification');

  return (
    <Box dir={i18n.dir()} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <BackButton onClick={() => navigate('/notifications', { state: { pageNo: state?.pageNo } })} />
      <SectionCard icon={<CampaignOutlinedIcon />} hue="accent" title={calmCaps(t('add_notification_title'))} id="notify-message">
        {/* One column up to md (as before); from lg the message sits beside who
            gets it and when, and the error and the button run under both. */}
        <Box
          component="form"
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
            handelSubmit();
          }}
          sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: 'repeat(2, minmax(0, 1fr))' }, columnGap: 3, rowGap: 2.25, alignItems: 'start', mt: 2 }}
        >
          {/* The message */}
          <Box sx={{ ...field, display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', rowGap: 2.25 }}>
            <Box sx={{ ...field, gridColumn: '1 / -1' }}>
              <FieldLabel htmlFor="notify-title">{t('title')}</FieldLabel>
              <TextField
                id="notify-title"
                fullWidth
                value={data.title}
                error={!!errors.title}
                onChange={(e) => setField('title', e.target.value)}
                inputProps={{ 'aria-describedby': describedBy('title') }}
              />
              <FieldError id="notify-title-error">{errors.title}</FieldError>
            </Box>
            <Box sx={{ ...field, gridColumn: '1 / -1' }}>
              <FieldLabel htmlFor="notify-body">{t('body')}</FieldLabel>
              <TextField
                id="notify-body"
                fullWidth
                multiline
                minRows={3}
                value={data.body}
                error={!!errors.body}
                onChange={(e) => setField('body', e.target.value)}
                inputProps={{ 'aria-describedby': describedBy('body') }}
              />
              <FieldError id="notify-body-error">{errors.body}</FieldError>
            </Box>
          </Box>

          {/* Who gets it, and when */}
          <Box sx={{ ...field, display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' }, columnGap: 2, rowGap: 2.25 }}>
            <Box sx={field}>
              <FieldLabel id="notify-usertype-label">{t('user_type')}</FieldLabel>
              <TextField
                select
                fullWidth
                value={data.usertype}
                error={!!errors.usertype}
                onChange={(e) => setField('usertype', e.target.value)}
                SelectProps={{ labelId: 'notify-usertype-label', SelectDisplayProps: { 'aria-describedby': describedBy('usertype') } }}
              >
                <MenuItem value="driver">{t('driver')}</MenuItem>
                <MenuItem value="customer">{t('customer')}</MenuItem>
              </TextField>
              <FieldError id="notify-usertype-error">{errors.usertype}</FieldError>
            </Box>
            <Box sx={field}>
              <FieldLabel id="notify-devicetype-label">{t('device_type')}</FieldLabel>
              <TextField
                select
                fullWidth
                value={data.devicetype}
                error={!!errors.devicetype}
                onChange={(e) => setField('devicetype', e.target.value)}
                SelectProps={{ labelId: 'notify-devicetype-label', SelectDisplayProps: { 'aria-describedby': describedBy('devicetype') } }}
              >
                <MenuItem value="All">{t('all')}</MenuItem>
                <MenuItem value="ANDROID">{t('android')}</MenuItem>
                <MenuItem value="IOS">{t('ios')}</MenuItem>
              </TextField>
              <FieldError id="notify-devicetype-error">{errors.devicetype}</FieldError>
            </Box>

            <Box sx={{ ...field, gridColumn: '1 / -1', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, minHeight: 44 }}>
              <FieldLabel htmlFor="notify-sms" sx={{ mb: 0 }}>
                {t('also_send_sms')}
              </FieldLabel>
              <Switch id="notify-sms" checked={sendSMS} onChange={(e) => setSendSMS(e.target.checked)} />
            </Box>

            <Box sx={{ ...field, gridColumn: '1 / -1', borderTop: `1px solid ${c.OUTLINE}`, pt: 2 }}>
              <FieldLabel id="notify-when-label">{t('send_option')}</FieldLabel>
              <ToggleButtonGroup
                exclusive
                color="primary"
                value={sendMode}
                onChange={(e, v) => {
                  if (v) setSendMode(v);
                }}
                aria-labelledby="notify-when-label"
                sx={{ flexWrap: 'wrap' }}
              >
                <ToggleButton value="now" sx={{ gap: 1, px: 2, textTransform: 'none', fontWeight: 600 }}>
                  <SendRoundedIcon fontSize="small" />
                  {t('send_now')}
                </ToggleButton>
                <ToggleButton value="schedule" sx={{ gap: 1, px: 2, textTransform: 'none', fontWeight: 600 }}>
                  <ScheduleRoundedIcon fontSize="small" />
                  {t('schedule_later')}
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>

            {sendMode === 'schedule' ? (
              <>
                <Box sx={{ ...field, gridColumn: '1 / -1' }}>
                  <FieldLabel htmlFor="notify-at">{t('schedule_datetime')}</FieldLabel>
                  <AddisDateTimeField
                    id="notify-at"
                    value={scheduledAt}
                    onChange={(ms) => {
                      setScheduledAt(ms);
                      setErrors((prev) => ({ ...prev, scheduledAt: null }));
                    }}
                    error={!!errors.scheduledAt}
                    describedBy={describedBy('scheduledAt')}
                  />
                  <FieldError id="notify-scheduledAt-error">{errors.scheduledAt}</FieldError>
                  <Box role="group" aria-label={t('quick_schedule')} sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mt: 1.5 }}>
                    <Typography component="span" sx={{ fontSize: 14, fontWeight: 600, color: c.INK_2, mr: 0.5 }}>
                      {t('quick_schedule')}
                    </Typography>
                    {QUICK_HOURS.map((h) => (
                      <FilterChip key={h} label={h >= 24 ? `${h / 24}d` : `${h}h`} selected={quickSelected(h)} onClick={() => handleQuickDelay(h)} />
                    ))}
                  </Box>
                </Box>

                <Box sx={{ ...field, gridColumn: '1 / -1' }}>
                  <FieldLabel id="notify-repeat-label">
                    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                      <RepeatRoundedIcon aria-hidden="true" sx={{ fontSize: 18, color: c.INK_2 }} />
                      {t('repeat')}
                    </Box>
                  </FieldLabel>
                  <Box role="radiogroup" aria-labelledby="notify-repeat-label" sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {[
                      { value: 'none', label: t('no_repeat') },
                      { value: 'daily', label: t('repeat_daily') },
                      { value: 'weekly', label: t('repeat_weekly') },
                      { value: 'custom', label: t('repeat_custom') },
                    ].map((o) => (
                      <FilterChip
                        key={o.value}
                        label={o.label}
                        selected={recurrenceFreq === o.value}
                        onClick={() => {
                          setRecurrenceFreq(o.value);
                          if (o.value !== 'custom') setRecurrenceDays([]);
                        }}
                      />
                    ))}
                  </Box>
                </Box>

                {recurrenceFreq === 'custom' ? (
                  <Box sx={{ ...field, gridColumn: '1 / -1' }}>
                    <FieldLabel id="notify-days-label">{t('select_days')}</FieldLabel>
                    <Box role="group" aria-labelledby="notify-days-label" sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {DAY_LABELS.map((day, idx) => (
                        <FilterChip
                          key={day}
                          label={t(day)}
                          selected={recurrenceDays.includes(idx)}
                          onClick={() => {
                            setRecurrenceDays((prev) => (prev.includes(idx) ? prev.filter((d) => d !== idx) : [...prev, idx].sort()));
                            setErrors((prev) => ({ ...prev, recurrenceDays: null }));
                          }}
                        />
                      ))}
                    </Box>
                    <FieldError id="notify-recurrenceDays-error">{errors.recurrenceDays}</FieldError>
                  </Box>
                ) : null}
              </>
            ) : null}
          </Box>

          {formError ? (
            <InlineMessage kind="error" sx={{ gridColumn: '1 / -1' }}>
              {formError}
            </InlineMessage>
          ) : null}

          <Box sx={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              type="submit"
              variant="contained"
              disabled={loding}
              startIcon={loding ? <CircularProgress size={16} color="inherit" /> : sendMode === 'now' ? <SendRoundedIcon /> : <ScheduleRoundedIcon />}
              sx={{ width: { xs: '100%', sm: 'auto' } }}
            >
              {loding ? L('sending', 'Sending…') : submitLabel}
            </Button>
          </Box>
        </Box>
      </SectionCard>
    </Box>
  );
};

export default AddNotifications;
